/**
 * Sprint D — Liste des modifications DPGF in-flight pour entreprise (multi-chantiers).
 *
 * Affiche les modifs en attente sur les chantiers où l'entreprise est invitée.
 * Chaque modif a un bouton "Répondre" qui ouvre une modale de réajustement de prix
 * (modification/ajout) ou d'accusé de réception (suppression).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Warning, X, Check, PencilSimple } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  modification: { label: 'Modification', color: '#C4773B', bg: '#FDF1E1' },
  ajout: { label: 'Ajout', color: '#2D6A4F', bg: '#E8F3EE' },
  suppression: { label: 'Suppression', color: '#C00000', bg: '#FCE8E8' },
};

export default function DPGFInflightModificationsList({ chantiers }) {
  const [allMods, setAllMods] = useState([]);  // [{...mod, chantier_id, chantier_name}]
  const [loading, setLoading] = useState(false);
  const [respondingMod, setRespondingMod] = useState(null);

  const reload = useCallback(async () => {
    if (!chantiers || chantiers.length === 0) return;
    setLoading(true);
    try {
      const all = [];
      for (const c of chantiers.slice(0, 10)) {
        try {
          const { data } = await axios.get(
            `${API_URL}/api/chantiers/${c.id}/dpgf/modifications-in-flight`,
            { params: { statut: 'en_attente' }, withCredentials: true },
          );
          (Array.isArray(data) ? data : []).forEach((m) => {
            all.push({ ...m, chantier_id: c.id, chantier_name: c.name });
          });
        } catch (e) {
          // 403/404 silent (pas de chantier ou pas en attente)
        }
      }
      setAllMods(all);
    } finally {
      setLoading(false);
    }
  }, [chantiers]);

  useEffect(() => { reload(); }, [reload]);

  if (!loading && allMods.length === 0) return null;

  return (
    <div className="border border-[#E0E0DB] bg-white" data-testid="dpgf-inflight-list">
      <div className="px-4 py-3 border-b border-[#E0E0DB] flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] flex items-center gap-2">
          <Warning size={16} className="text-[#C4773B]" weight="fill" />
          DPGF — Modifications à valider
          <span className="ml-1 px-2 py-0.5 text-[10px] bg-[#C4773B] text-white">{allMods.length}</span>
        </h2>
      </div>
      <div className="divide-y divide-[#E0E0DB]">
        {allMods.map((m) => {
          const typeCfg = TYPE_LABELS[m.type] || TYPE_LABELS.modification;
          const refData = m.nouveau || m.ancien || {};
          return (
            <div
              key={m.id}
              className="px-4 py-3 flex items-start gap-3 hover:bg-[#FAFAF8]"
              data-testid={`dpgf-inflight-${m.id}`}
            >
              <span
                className="mt-1 px-2 py-1 text-[10px] uppercase tracking-wider font-bold flex-shrink-0"
                style={{ background: typeCfg.bg, color: typeCfg.color }}
              >
                {typeCfg.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#2C2C2C]">
                  {refData.designation || '(sans désignation)'}
                </div>
                <div className="text-xs text-[#6B6B6B] mt-0.5">
                  {m.chantier_name} ·
                  {m.type !== 'suppression' && refData.quantite != null && (
                    <span> {refData.quantite} {refData.unite}</span>
                  )}
                  <span className="ml-2 text-[#9A9A9A]">
                    par {m.modifie_par_name || '?'}
                  </span>
                </div>
              </div>
              <Button
                size="sm"
                className="rounded-none bg-[#1E3A5F] hover:bg-[#152a48] text-white text-xs"
                onClick={() => setRespondingMod(m)}
                data-testid={`dpgf-inflight-respond-${m.id}`}
              >
                Répondre
              </Button>
            </div>
          );
        })}
      </div>

      {respondingMod && (
        <DPGFInflightResponseDialog
          mod={respondingMod}
          onClose={() => setRespondingMod(null)}
          onResponded={() => { setRespondingMod(null); reload(); }}
        />
      )}
    </div>
  );
}


function DPGFInflightResponseDialog({ mod, onClose, onResponded }) {
  const ref = mod.nouveau || mod.ancien || {};
  const [accepte, setAccepte] = useState(true);
  const [nouveauPrix, setNouveauPrix] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isSuppression = mod.type === 'suppression';

  const submit = async () => {
    if (submitting) return;
    if (!isSuppression && accepte && (!nouveauPrix || parseFloat(nouveauPrix) < 0)) {
      toast.error('Renseignez votre nouveau prix unitaire HT');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        accepte,
        commentaire: commentaire.trim() || null,
      };
      if (!isSuppression && accepte) {
        payload.nouveau_prix_unitaire_ht = parseFloat(nouveauPrix);
      }
      await axios.post(
        `${API_URL}/api/chantiers/${mod.chantier_id}/dpgf/modifications-in-flight/${mod.id}/reponse-entreprise`,
        payload,
        { withCredentials: true },
      );
      toast.success(isSuppression ? 'Réception confirmée' : 'Réponse enregistrée');
      onResponded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="dpgf-inflight-response-dialog"
    >
      <div className="bg-white shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#E0E0DB] flex items-center justify-between bg-[#FAFAF8]">
          <h3 className="text-base font-bold text-[#2C2C2C]">
            {isSuppression ? 'Poste retiré du marché' : (mod.type === 'ajout' ? 'Nouveau poste à chiffrer' : 'Modification de poste')}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-white" data-testid="dpgf-inflight-response-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 text-sm">
          <div className="bg-[#FAFAF8] border-l-4 border-[#1E3A5F] p-2 text-xs text-[#6B6B6B]">
            <div className="font-semibold text-[#2C2C2C]">{ref.designation || '(sans désignation)'}</div>
            {!isSuppression && (
              <div>{ref.quantite} {ref.unite}</div>
            )}
            {mod.type === 'modification' && mod.ancien && (
              <div className="text-[#9A9A9A] mt-1 italic">
                Ancien : {mod.ancien.designation} ({mod.ancien.quantite} {mod.ancien.unite})
              </div>
            )}
          </div>

          {isSuppression ? (
            <p className="text-sm text-[#2C2C2C]">
              Ce poste a été retiré du DPGF. Cliquez "OK, j'ai pris connaissance" pour
              confirmer la réception. Votre ligne sera marquée comme retirée dans votre offre.
            </p>
          ) : (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-bold mb-1">Action</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setAccepte(true)}
                    className={`px-2 py-2 text-xs border rounded-none ${
                      accepte ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]' : 'bg-white text-[#2C2C2C] border-[#E0E0DB]'
                    }`}
                    data-testid="dpgf-inflight-accept"
                  >
                    <Check size={12} className="inline mr-1" /> Réajuster mon prix
                  </button>
                  <button
                    onClick={() => setAccepte(false)}
                    className={`px-2 py-2 text-xs border rounded-none ${
                      !accepte ? 'bg-[#C00000] text-white border-[#C00000]' : 'bg-white text-[#2C2C2C] border-[#E0E0DB]'
                    }`}
                    data-testid="dpgf-inflight-refuse"
                  >
                    <X size={12} className="inline mr-1" /> Refuser
                  </button>
                </div>
              </div>

              {accepte && (
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">
                    Nouveau prix unitaire HT (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={nouveauPrix}
                    onChange={(e) => setNouveauPrix(e.target.value)}
                    className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm focus:outline-none focus:border-[#1E3A5F]"
                    placeholder="Ex : 380"
                    data-testid="dpgf-inflight-prix"
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">
              Commentaire (optionnel)
            </label>
            <Textarea
              rows={2}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              maxLength={500}
              className="rounded-none"
              data-testid="dpgf-inflight-commentaire"
            />
          </div>
        </div>

        <div className="p-4 border-t border-[#E0E0DB] flex justify-end gap-2 bg-[#FAFAF8]">
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button
            className="rounded-none bg-[#1E3A5F] hover:bg-[#152a48] text-white"
            onClick={submit}
            disabled={submitting || (!isSuppression && accepte && !nouveauPrix)}
            data-testid="dpgf-inflight-submit"
          >
            {submitting ? 'Envoi…' : (isSuppression ? 'OK, j\'ai pris connaissance' : 'Envoyer la réponse')}
          </Button>
        </div>
      </div>
    </div>
  );
}
